import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from './lib/supabase';

function ResetPassword() {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [sessionReady, setSessionReady] = useState(false);

  useEffect(() => {
    // Supabase sends the user here with a recovery token in the URL hash
    // The client library auto-picks it up and creates a session
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') {
        setSessionReady(true);
      }
    });

    // Also check if session already exists (user might already be recovered)
    supabase.auth.getSession().then(({ data }) => {
      if (data?.session) setSessionReady(true);
    });

    return () => subscription.unsubscribe();
  }, []);

  const handleReset = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      const { error: updateError } = await supabase.auth.updateUser({ password });
      if (updateError) throw updateError;
      setSuccess(true);
    } catch (err) {
      setError(err.message || 'Failed to update password');
    }
    setLoading(false);
  };

  const accent = '#ff6b2b';
  const accent2 = '#ffab40';

  const inputStyle = {
    width: '100%', padding: '12px 16px', background: '#252840', border: '1px solid #2a2a4a',
    borderRadius: '10px', color: '#fff', fontSize: '15px', outline: 'none', boxSizing: 'border-box',
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px', background: '#0a0a1a' }}>
      <div style={{ maxWidth: '420px', width: '100%' }}>
        <div style={{ textAlign: 'center', marginBottom: '36px' }}>
          <div style={{
            width: '60px', height: '60px', borderRadius: '16px',
            background: `linear-gradient(135deg, ${accent}, ${accent2})`,
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: '16px',
          }}>
            <span style={{ fontSize: '24px', fontWeight: '900', color: '#111' }}>iV</span>
          </div>
          <h1 style={{ color: '#fff', fontSize: '28px', fontWeight: '800', margin: '0 0 6px' }}>
            {success ? 'Password Updated!' : 'Set New Password'}
          </h1>
          <p style={{ color: '#888', fontSize: '15px', margin: 0 }}>
            {success ? 'You can now sign in with your new password' : 'Enter your new password below'}
          </p>
        </div>

        <div style={{ background: '#12121f', borderRadius: '20px', padding: '32px', border: '1px solid #1e1e35' }}>
          {error && (
            <div style={{ background: '#d6303115', color: '#ff6b6b', padding: '12px 16px', borderRadius: '10px', marginBottom: '20px', fontSize: '14px' }}>{error}</div>
          )}

          {success ? (
            <div>
              <div style={{ background: '#10b98120', borderRadius: 12, padding: '20px', marginBottom: 20, textAlign: 'center' }}>
                <div style={{ fontSize: 36, marginBottom: 10 }}>✅</div>
                <div style={{ color: '#10b981', fontWeight: 700, fontSize: 16 }}>Password changed successfully</div>
              </div>
              <button onClick={() => navigate('/login')} style={{
                width: '100%', padding: '14px', background: `linear-gradient(135deg, ${accent}, ${accent2})`,
                border: 'none', borderRadius: '12px', color: '#fff', fontSize: '16px', fontWeight: '700', cursor: 'pointer',
              }}>Go to Sign In →</button>
            </div>
          ) : !sessionReady ? (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <div style={{ color: '#888', fontSize: 14, marginBottom: 16 }}>Verifying your reset link...</div>
              <div style={{ color: '#555', fontSize: 13 }}>If this takes too long, the link may have expired.</div>
              <button onClick={() => navigate('/login')} style={{
                marginTop: 20, background: 'none', border: 'none', color: accent, fontSize: '14px', fontWeight: '600', cursor: 'pointer',
              }}>← Back to Sign In</button>
            </div>
          ) : (
            <form onSubmit={handleReset}>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', color: '#aaa', fontSize: '13px', fontWeight: '600', marginBottom: '6px' }}>New Password</label>
                <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
                  autoComplete="new-password" style={inputStyle} placeholder="New password (6+ chars)" />
              </div>
              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', color: '#aaa', fontSize: '13px', fontWeight: '600', marginBottom: '6px' }}>Confirm Password</label>
                <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required
                  autoComplete="new-password" style={inputStyle} placeholder="Confirm new password" />
              </div>
              <button type="submit" disabled={loading} style={{
                width: '100%', padding: '14px', background: `linear-gradient(135deg, ${accent}, ${accent2})`,
                border: 'none', borderRadius: '12px', color: '#fff', fontSize: '16px', fontWeight: '700',
                cursor: loading ? 'wait' : 'pointer', opacity: loading ? 0.7 : 1,
              }}>{loading ? 'Updating...' : 'Update Password'}</button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

export default ResetPassword;
